import type {
  AutoJudgmentCriteria,
  ContractGeneration,
  InterestClassBookSetting,
  OwnershipRightsNonTrfFinance,
} from '@libs/enums';
import type { ItemAttributes } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  ItemName,
  type ItemNames,
  Locale,
  type OrEmpty,
  SingleValue,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  // 帳簿
  book: number;

  // 名称
  name: string;

  // ラベル名称
  labelName: string;

  // 帳簿のラベル名称Id
  readonly bookLabelId: number | null;

  //  帳簿の番号
  readonly key: number;

  // 帳簿名称
  readonly bookName: string;

  // 簿価名称(配列)
  readonly bookNames: string[];

  // 簿価値(配列)
  readonly bookValues: string[];

  // IFRS帳簿
  readonly ifrsBook: boolean;

  // 増減簿価設定
  readonly increDecreBookValSet: string;

  // 約定・発生区分
  readonly contractGenerationClass: OrEmpty<ContractGeneration>;

  // 自動判定基準
  readonly autoJudgeBasis: OrEmpty<AutoJudgmentCriteria>;

  // 所有権移転外ファイナンス・リースの扱い
  readonly ownershipRightsNonTrfFinance: OrEmpty<OwnershipRightsNonTrfFinance>;

  // 利息区分(帳簿設定)
  readonly interestClassBookSetting: OrEmpty<InterestClassBookSetting>;
}

interface Record {
  readonly bookId: number;
  readonly name: string;
  readonly labelName: string;
  readonly bookLabelId: number | null;
  readonly key: number;
  readonly bookName: string;
  readonly bookNames: string[];
  readonly bookValues: string[];
  readonly ifrsBook: boolean;
  readonly increDecreBookValSet: string;
  readonly contractGenerationClass: string;
  readonly autoJudgeBasis: string;
  readonly ownershipRightsNonTrfFinance: string;
  readonly interestClassBookSetting: string;
}

const attributes = {
  name: Characters.preset.freeFormat(),
  labelName: Characters.preset.freeFormat(),
  bookName: Characters.preset.freeFormat({ maxLen: 10 }),
  ifrsBook: Bool.new(),
  increDecreBookValSet: SingleValue.shortValue(true),
  contractGenerationClass: SingleValue.shortValue(true),
  autoJudgeBasis: SingleValue.shortValue(),
  ownershipRightsNonTrfFinance: SingleValue.shortValue(),
  interestClassBookSetting: SingleValue.shortValue(true),
} satisfies ItemAttributes<Schema>;

const names = {
  book: ItemName.of([Locale.ja, '帳簿']),
  name: ItemName.of([Locale.ja, '名称']),
  labelName: ItemName.of([Locale.ja, 'ラベル名称']),
  bookName: ItemName.of([Locale.ja, '帳簿名称'], [Locale.en, 'Book Name']),
  ifrsBook: ItemName.of([Locale.ja, 'IFRS帳簿'], [Locale.en, 'Ifrs Book']),
  increDecreBookValSet: ItemName.of(
    [Locale.ja, '増減簿価設定'],
    [Locale.en, 'Increase / Decrease Book Value Setting'],
  ),
  contractGenerationClass: ItemName.of([Locale.ja, '約定・発生'], [Locale.en, 'Contract Generation Class']),
  autoJudgeBasis: ItemName.of([Locale.ja, '自動判定基準'], [Locale.en, 'Auto Judge Basis']),
  ownershipRightsNonTrfFinance: ItemName.of(
    [Locale.ja, '所有権移転外ファイナンス・リースの扱い'],
    [Locale.en, 'Ownership Rights Non Transfer Finance'],
  ),
  interestClassBookSetting: ItemName.of([Locale.ja, '利息区分'], [Locale.en, 'Interest Class']),
} satisfies ItemNames<Schema>;

const labelNames = {
  basicBookOne: ItemName.of([Locale.ja, '第1帳簿'], [Locale.en, 'Book One']),
  basicBookTwo: ItemName.of([Locale.ja, '第2帳簿'], [Locale.en, 'Book Two']),
  basicBookThree: ItemName.of([Locale.ja, '第3帳簿'], [Locale.en, 'Book Three']),
  basicBookFour: ItemName.of([Locale.ja, '第4帳簿'], [Locale.en, 'Book Four']),
  basicBookFive: ItemName.of([Locale.ja, '第5帳簿'], [Locale.en, 'Book Five']),
  basicBookSix: ItemName.of([Locale.ja, '第6帳簿'], [Locale.en, 'Book Six']),
};

const titleNames = {
  bookDetailsTitle: ItemName.of(
    [Locale.ja, '会計方針設定-帳簿の設定'],
    [Locale.en, 'Accounting Policy Settings - Book Settings'],
  ),
  bookDetailsFieldTitle: ItemName.of([Locale.ja, '帳簿の設定'], [Locale.en, 'Book settings']),
  bookName: ItemName.of([Locale.ja, '帳簿名称'], [Locale.en, 'Book Name']),
  isUsed: ItemName.of([Locale.ja, '使用有無'], [Locale.en, 'isUsed']),
  ifrsBook: ItemName.of([Locale.ja, 'IFRS帳簿'], [Locale.en, 'Ifrs Book']),
  increDecreBookValSet: ItemName.of(
    [Locale.ja, '増減簿価設定'],
    [Locale.en, 'Increase / Decrease Book Value Setting'],
  ),
};

interface BookSetting {
  schema: Schema;
  record: Record;
}

export const bookSetting = {
  names,
  labelNames,
  titleNames,
  attributes,
};

export type { BookSetting };
